import bindAllOfThis from 'src/utils/BindThisHelper';
import ImageApi from 'src/utils/ImageHelper';
import { PhaserColorWheel } from '../../shared/ColorWheel';
import { EndGameScene } from './EndGameScene';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import TranslateApi from '../../../utils/TranslateHelper';
import Languages from '../../../constants/Languages';

// Paintbrush helper class to hold drawing attributes
class PaintBrush {
    public thickness: number;
    public color: number;
    public alpha: number;

    // default color yellow, thickness 5
    constructor() {
        this.thickness = 12;
        this.color = 0x223e93;
        this.alpha = 1;
    }
}

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    // Easel variables
    private easelControls: Phaser.Geom.Rectangle;
    // Paintbrush holds draw stroke attributes
    private paintBrush: PaintBrush = new PaintBrush();
    // Arrays to hold history and colors
    private lineHistory: Phaser.Geom.Point[];
    private drawing: boolean;
    private finishButton: Phaser.GameObjects.Sprite;
    private alien: Phaser.GameObjects.Sprite;
    private stickers: Phaser.GameObjects.Image[];
    private alienTexture: Phaser.GameObjects.RenderTexture;
    private alienGraphic: Phaser.GameObjects.Graphics;
    private drawArea: Phaser.Geom.Polygon;
    private div: HTMLDivElement = document.createElement('div');
    private currentImage: Phaser.GameObjects.Image;
    private paste: Phaser.GameObjects.Image;
    private pastedImages: Phaser.GameObjects.Image[];
    private paintGunCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/paint_gun.cur") + '), pointer';
    private drawingMode: boolean = false;

    public static FACE: number;

    public static CROPPED_IMAGE: string = null;

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        PhaserColorWheel.preload(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.input.setDefaultCursor(this.paintGunCursor);
        this.map = this.make.tilemap({ key: 'map' });
        bindAllOfThis(this, MainScene.prototype);
    }

    create(): void {
        this.initialize();
        this.buildBackgroundContent();

        this.stickers = [];
        this.lineHistory = [];
        this.pastedImages = [];
        this.drawing = false;
        this.drawingMode = true;
        document.body.appendChild(this.div);

        // Register the click events
        this.registerEvents();
    }

    buildBackgroundContent() {
        let background = this.add.image(0, 0, "background").setOrigin(0);

        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "cover") as TiledGameObject;
        let artBoard = this.add.image(ob.x, ob.y, "assets", "cover");
        artBoard.setOrigin(0);
        this.easelControls = artBoard.getBounds();

        ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "alien") as TiledGameObject;

        this.alien = this.add.sprite(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "bigHead" + MainScene.FACE);
        this.alien.setInteractive();

        this.buildEasel();
        this.buildButton();
        this.createTattoos();
        this.buildColorWheel();
    }

    buildEasel(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "drawArea" + MainScene.FACE) as TiledGameObject;
        var points = ob["polygon"];
        points.forEach((p) => {
            p.x += ob.x;
            p.y += ob.y;
        });

        this.drawArea = new Phaser.Geom.Polygon(points);

        this.alien.on('pointerdown', (pointer) => {
            this.pasteImage(pointer);
        });

        this.alienTexture = this.add.renderTexture(0, 0, this.gameWidth, this.gameHeight);
        this.alienTexture.setOrigin(0);
        this.alienTexture.setDepth(0);

        this.alienGraphic = this.add.graphics();
        this.alienGraphic.setDepth(0);
    }

    private canPaste(): boolean {
        // Don't even have a sticker right now
        if (!this.currentImage.visible) {
            return false;
        }

        return this.inDrawArea();
    }

    inDrawArea(): boolean {
        var bounds = this.currentImage.getBounds();
        var center = this.currentImage.getCenter();
        bounds.width *= 0.1;
        bounds.height *= 0.1;
        bounds.centerX = center.x;
        bounds.centerY = center.y;

        if (this.drawArea.contains(center.x, center.y)) {
            return true;
        }

        // Check if it contains any of the 4 corners (polygons don't have an intersects method)
        if (this.drawArea.contains(bounds.left, bounds.bottom) ||
            this.drawArea.contains(bounds.right, bounds.bottom) ||
            this.drawArea.contains(bounds.left, bounds.top) ||
            this.drawArea.contains(bounds.right, bounds.top)) {
            return true;
        }

        return false;
    }

    pasteImage(pointer) {
        if (this.canPaste()) {
            this.paste = this.add.image(pointer.x, pointer.y, "assets", this.currentImage.frame.name);
            this.paste.setInteractive();
            this.input.setDraggable(this.paste);
            this.pastedImages.push(this.paste);
        }
    }

    buildButton(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "button") as TiledGameObject;
        this.finishButton = this.add.sprite(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", TranslateApi.currentLanguage === Languages.Spanish ? "button_sp" : "button");
        this.finishButton.setInteractive({ useHandCursor: true });
        this.finishButton.on("pointerdown", () => {
            this.drawingMode = false;
            this.currentImage.setVisible(false);
            // Screenshot for later
            this.registry.parent.renderer.snapshot((image: HTMLImageElement) => {
                image.onload = () => {
                    var croppedImage = ImageApi.CropImageToDataURI(image, 0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
                    this.endGame(croppedImage);
                };
            });
        });
    }

    buildColorWheel(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "colorwheel") as TiledGameObject;
        var colorWheel = new PhaserColorWheel(this, ob.x, ob.y, ob.width, ob.height);
        colorWheel.on("colorChange", (color: Phaser.Display.Color) => {
            this.drawingMode = true;
            this.paintBrush.color = color.color;
            this.refreshPaintBrush();
            this.input.setDefaultCursor(this.paintGunCursor);
            this.currentImage.setVisible(false);
        });
        colorWheel.setDepth(2);
    }

    createTattoos() {
        this.currentImage = this.add.image(0, 0, "assets", "tattoo1");
        this.currentImage.setDepth(1);
        this.currentImage.setVisible(false);
        this.stickers = [];

        this.map.getObjectLayer('Tattoos').objects.forEach((ob: TiledGameObject) => {
            this.createSticker(ob.x + ob.width / 2, ob.y + ob.height / 2, ob.name);
        });
    }

    createSticker(x, y, frame) {
        var sticker = this.add.image(x, y, 'assets', frame);
        sticker.setDepth(2);
        sticker.setInteractive();

        sticker.on("pointerdown", () => {
            this.drawingMode = false;
            this.drawing = false;
            this.currentImage.setFrame(sticker.frame.name);
            this.currentImage.setVisible(true);
            this.input.setDefaultCursor('none');
        });
        this.stickers.push(sticker);
    }

    refreshPaintBrush() {
        this.alienGraphic.lineStyle(this.paintBrush.thickness, this.paintBrush.color, this.paintBrush.alpha);
    }

    registerEvents(): void {
        this.input.on('pointerdown', this.pointerDown);
        this.input.on('pointerup', this.pointerUp);
        this.input.on('pointermove', this.pointerMove);
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            if (!this.drawingMode) {
                this.children.bringToTop(gameObject);
                this.drawingMode = false;
                gameObject.x = pointer.downX;
                gameObject.y = pointer.downY;
            }
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, dragX: number, dragY: number) => {
            if (this.drawingMode) {
                return;
            }
            gameObject.x = pointer.x;
            gameObject.y = pointer.y;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            // If we're drawing, no need to do anything
            if (this.drawing || this.drawingMode) {
                return;
            }

            if (!this.inDrawArea()) {
                gameObject.destroy();
            }
            else {
                this.children.moveDown(gameObject);
            }
        });
    }

    canDraw(pointer: Phaser.Input.Pointer) {
        if (this.drawArea.contains(pointer.x, pointer.y)) {
            return true;
        }

        return false;
    }

    pointerDown(pointer: Phaser.Input.Pointer): void {
        if (this.drawingMode && this.canDraw(pointer)) {
            this.drawing = true;
        }

        this.lineHistory = [];
    }

    pointerUp(pointer: Phaser.Input.Pointer): void {
        this.drawing = false;
        // Capture the graphics as a texture
        this.alienGraphic.generateTexture("background-drawn", this.sys.canvas.width, this.sys.canvas.height);
        this.alienGraphic.clear();

        let tempImage: Phaser.GameObjects.Image = this.add.image(0, 0, "background-drawn");
        this.alienTexture.save();
        this.alienTexture.draw(tempImage.texture, tempImage.frame, 0, 0);
        this.alienTexture.restore();

        tempImage.destroy();
    }

    pointerMove(pointer: Phaser.Input.Pointer): void {
        if (this.drawing && this.canDraw(pointer)) {
            this.alienGraphic.clear();
            this.refreshPaintBrush();
            this.lineHistory.push(new Phaser.Geom.Point(pointer.x, pointer.y));
            // Do the drawing
            this.alienGraphic.strokePoints(this.lineHistory);
        }

        this.currentImage.x = pointer.x;
        this.currentImage.y = pointer.y;

        if (this.drawingMode) {
            this.currentImage.setVisible(false);
            this.input.setDefaultCursor(this.paintGunCursor);
        }
        else {
            this.currentImage.setVisible(true);
            this.input.setDefaultCursor("none");
            if (this.isMovingExistingImage(pointer)) {
                this.currentImage.setVisible(false);
                this.input.setDefaultCursor('-webkit-grabbing');
            }
        }

        if (this.easelControls.contains(this.input.activePointer.x, this.input.activePointer.y)) {
            this.currentImage.setVisible(false);
            this.input.setDefaultCursor('pointer');
        }
    }

    isMovingExistingImage(pointer: Phaser.Input.Pointer): boolean {
        for (var i of this.pastedImages) {
            if (i.getBounds().contains(pointer.x, pointer.y)) {
                return true;
            }
        }
        return false;
    }

    endGame(image: string): void {
        MainScene.CROPPED_IMAGE = image;
        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

    drawPolygonDrawingArea() {
        // Draw polygon and set it up
        var graphics = this.add.graphics();
        graphics.lineStyle(2, 0x000000);
        graphics.beginPath();
        graphics.moveTo(this.drawArea.points[0].x, this.drawArea.points[0].y);
        for (var i = 1; i < this.drawArea.points.length; i++) {
            graphics.lineTo(this.drawArea.points[i].x, this.drawArea.points[i].y);
        }
        graphics.closePath();
        graphics.strokePath();
    }
}



// WEBPACK FOOTER //
// ./src/games/FacePainting/scenes/MainScene.ts